import {useState} from 'react';
import {Box, Button, Grid, TextField, Typography} from '@mui/material';
import {useSearchParams} from 'react-router-dom';
import {useMutation, useQuery} from '@tanstack/react-query';
import {getLocalPasswordResetToken, resetLocalPassword} from '../services/AccountService';
import authBg from '../assets/images/mfx_auth_bg.jpg';
import mfxLogo from '../assets/images/mfx_logo.png';

// Shared centered message view with background
function CenteredBackgroundMessage({text}) {
  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          backgroundImage: `url(${authBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'blur(5px)',
          zIndex: -1,
        }}
      />
      <Grid container spacing={1} justifyContent="center" alignItems="center" height="100%">
        <Typography variant="h6" align="center">
          {text}
        </Typography>
      </Grid>
    </>
  );
}

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [mismatchError, setMismatchError] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const {data: tokenData, isLoading: tokenLoading, isError: tokenError} = useQuery({
    queryKey: ['localPasswordResetToken', token],
    queryFn: () => getLocalPasswordResetToken(token),
    // Only call the API when a token is provided
    enabled: !!token,
    retry: false,
  });

  const {mutate: doResetPassword, isLoading: resetting} = useMutation({
    mutationFn: resetLocalPassword,
    onSuccess: () => setShowSuccess(true),
    onError: () => setSubmitError(true),
  });

  const handleConfirm = () => {
    if (password === password2) {
      setSubmitError(false);
      doResetPassword({newPassword: password, token});
    } else {
      setMismatchError(true);
    }
  };

  // If no token provided OR provided token is invalid, show shared centered message
  if (!showSuccess && (!token || tokenError)) {
    return <CenteredBackgroundMessage text="Invalid token!" />;
  }

  if (submitError) {
    return <CenteredBackgroundMessage text="Reset password failed! Please try again." />;
  }

  if (showSuccess) {
    return <CenteredBackgroundMessage text="Your password has been reset successfully!" />;
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          backgroundImage: `url(${authBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          // filter: 'blur(5px)',
          zIndex: -1,
        }}
      />
      <Grid container spacing={1} justifyContent="center" alignItems="center" height="100%">
        <Grid item xs={11} sm={9} md={5} lg={4} xl={3}>
          <Box sx={{p: 2}}>
            <Box
              component="img"
              src={mfxLogo}
              alt="MFX Logo"
              sx={{
                display: 'block',
                mx: 'auto',
                mb: 3,
                height: '3rem',
                width: 'auto',
                objectFit: 'contain',
              }}
            />
            <Typography
              variant="h5"
              align="center"
              sx={{ color: 'black', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.1em', fontSize: '1.75rem' }}
            >
              Reset Password
            </Typography>
            <Typography align="center" sx={{ color: 'black', mt: 3 }}>
              {token ? (tokenData?.email || '') : 'email@address.com'}
            </Typography>
            <Box pt={4} pb={3}>
              <Box component="form" role="form">
                <Box mb={2}>
                  <TextField
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (mismatchError) setMismatchError(false);
                    }}
                    type="password"
                    label="New Password"
                    InputLabelProps={{shrink: !!password}}
                    sx={{
                      // Match Confirm button height (3rem) and keep vertical centering
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '1.5rem',
                        backgroundColor: 'white',
                        height: '3rem',
                      },
                      '& .MuiOutlinedInput-input': {
                        height: '3rem',
                        lineHeight: '3rem',
                        padding: '0 1rem',
                      },
                      // Keep floating label centered vertically when not shrunk
                      '& .MuiInputLabel-root': {
                        top: '50%',
                        transform: 'translate(14px, -50%)',
                      },
                      // Restore default-ish position when label shrinks
                      '& .MuiInputLabel-shrink': {
                        top: 0,
                        transform: 'translate(14px, -9px) scale(0.75)',
                      },
                    }}
                    fullWidth
                  />
                </Box>
                <Box mb={2}>
                  <TextField
                    value={password2}
                    onChange={(e) => {
                      setPassword2(e.target.value);
                      if (mismatchError) setMismatchError(false);
                    }}
                    type="password"
                    label="Re-enter New Password"
                    error={mismatchError}
                    helperText={mismatchError ? 'Passwords do not match' : ''}
                    InputLabelProps={{shrink: !!password2}}
                    sx={{
                      // Match Confirm button height (3rem) and keep vertical centering
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '1.5rem',
                        backgroundColor: 'white',
                        height: '3rem',
                      },
                      '& .MuiOutlinedInput-input': {
                        height: '3rem',
                        lineHeight: '3rem',
                        padding: '0 1rem',
                      },
                      // Keep floating label centered vertically when not shrunk
                      '& .MuiInputLabel-root': {
                        top: '50%',
                        transform: 'translate(14px, -50%)',
                      },
                      // Restore default-ish position when label shrinks
                      '& .MuiInputLabel-shrink': {
                        top: 0,
                        transform: 'translate(14px, -9px) scale(0.75)',
                      },
                    }}
                    fullWidth
                  />
                </Box>
                <Box mt={2}>
                  <Button variant="contained" color="primary" fullWidth onClick={handleConfirm} disabled={resetting}>
                    <Typography>Confirm</Typography>
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}
